import React from "react";
import { Col, Row } from "reactstrap";
import DashPanel from "../common/DashPanel";
import SectionTitle from "../common/SectionTitle";
import ChipRow from "../common/ChipRow";
import ProgressRow from "../common/ProgressRow";
import ResourceGauge from "../common/ResourceGauge";
import ServerLogRow from "../common/ServerLogRow";
import { T } from "../../constants/theme";
import { serverData, systemHealthData } from "../../data/dashboardMockData";

const STORAGE_COLORS = [T.blue, T.green, T.orange, T.teal];

const SystemHealthSection = ({ loading }) => (
  <Col xl={7} className="d-flex">
    <DashPanel title="System Health" icon="bx bx-server" iconColor={T.teal} loading={loading}>
      <Row>
        <Col md={6}>
          <SectionTitle>Server Resources</SectionTitle>
          <ResourceGauge
            label="CPU"
            used={serverData.cpu.usedPercent}
            total={100}
            unit="%"
            color={T.blue}
            loading={loading}
          />
          <ResourceGauge
            label="RAM"
            used={serverData.ram.usedGB}
            total={serverData.ram.totalGB}
            unit=" GB"
            color={T.purple}
            loading={loading}
          />
          <ResourceGauge
            label="Storage"
            used={serverData.storage.usedGB}
            total={serverData.storage.totalGB}
            unit=" GB"
            color={T.orange}
            loading={loading}
          />

          <SectionTitle style={{ marginTop: "12px" }}>Storage Breakdown</SectionTitle>
          {serverData.storage.breakdown.map((b, i) => (
            <ProgressRow
              key={i}
              label={b.label}
              value={`${b.gb} GB`}
              max={serverData.storage.usedGB || 1}
              rawPct={Math.round((b.gb / serverData.storage.usedGB) * 100)}
              color={STORAGE_COLORS[i % 4]}
              loading={loading}
            />
          ))}
        </Col>

        <Col md={6}>
          <SectionTitle>Database</SectionTitle>
          <div style={{ fontSize: "12px", color: T.muted, marginBottom: "8px" }}>
            {(systemHealthData.database.sizeMB / 1024).toFixed(1)} GB total ·{" "}
            {systemHealthData.database.tableCount} tables · Last backup:{" "}
            {systemHealthData.database.lastBackup}
          </div>
          <ChipRow
            items={[
              {
                label: "Uptime",
                value: `${systemHealthData.database.uptimePercent}%`,
                color: T.green,
              },
              {
                label: "Slow Queries",
                value: systemHealthData.database.slowQueryCount,
                color:
                  systemHealthData.database.slowQueryCount > 5 ? T.red : T.yellow,
              },
              {
                label: "Connections",
                value: `${systemHealthData.database.connectionPool.used}/${systemHealthData.database.connectionPool.max}`,
                color: T.blue,
              },
            ]}
          />

          <SectionTitle>API Health</SectionTitle>
          <ChipRow
            items={[
              {
                label: "Uptime",
                value: `${systemHealthData.api.uptimePercent}%`,
                color: T.green,
              },
              {
                label: "Avg Resp",
                value: `${systemHealthData.api.avgResponseMs}ms`,
                color: T.teal,
              },
              {
                label: "Error Rate",
                value: `${systemHealthData.api.errorRate}%`,
                color: T.orange,
              },
            ]}
          />

          {systemHealthData.api.endpointHealth.map((ep, i) => (
            <div
              key={i}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "4px 0",
                borderBottom: "1px solid #f1f3f5",
                fontSize: "11px",
              }}
            >
              <span style={{ color: T.text, fontFamily: "monospace" }}>{ep.endpoint}</span>
              <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                <span style={{ color: T.muted }}>{ep.avgMs}ms</span>
                <span
                  style={{
                    padding: "1px 6px",
                    borderRadius: "4px",
                    fontWeight: 600,
                    background: ep.status === "OK" ? "#e8f5e9" : "#fff8e1",
                    color: ep.status === "OK" ? "#2e7d32" : "#f57f17",
                  }}
                >
                  {ep.status}
                </span>
              </div>
            </div>
          ))}

          <SectionTitle style={{ marginTop: "12px" }}>Large Export Alerts</SectionTitle>
          {serverData.bigExportAlerts.map((a, i) => (
            <div
              key={i}
              style={{
                padding: "5px 0",
                borderBottom: "1px solid #f1f3f5",
                fontSize: "11px",
              }}
            >
              <div style={{ fontWeight: 600, color: T.text }}>{a.user}</div>
              <div style={{ color: T.muted }}>
                {a.file} ·{" "}
                <span style={{ color: T.orange, fontWeight: 700 }}>{a.sizeMB}MB</span> · {a.time}
              </div>
            </div>
          ))}
        </Col>
      </Row>

      <SectionTitle style={{ marginTop: "14px" }}>Recent Server Logs</SectionTitle>
      <div className="sad-scroll-list sad-scroll-list--sm">
        {serverData.recentLogs.map((log, i) => (
          <ServerLogRow key={i} log={log} />
        ))}
      </div>
    </DashPanel>
  </Col>
);

export default SystemHealthSection;
